import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { EModalOutput, IModalButton } from 'src/app/modules/shared/components/basic-modal/basic-modal.constants';
import { IFormKey, IFormSelectOption } from 'src/app/modules/shared/models/forms';
import { ICityObject } from 'src/app/modules/shared/models/policy.model';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { document_change_form_keys } from '../document-change/document-change.constants';
import { effective_owner_registry_form_keys, isv_contractor_address_form_keys, isv_contractor_business_form_keys, isv_contractor_consents_form_keys, isv_contractor_PG_consents_form_keys, isv_contractor_registry_form_keys, isv_contractor_residence_form_keys, isv_legal_guardian_registry_form_keys, isv_new_contractor_business_form_keys, isv_new_contractor_PG_registry_form_keys, isv_new_contractor_registry_form_keys, isv_PG_contractor_business_form_keys, isv_PG_contractor_registry_form_keys, isv_PG_new_contractor_business_form_keys, proper_verification_form_keys, proper_verification_tutor_form_keys } from './registry-form.constants';
import { TYPES_WITH_TAE_REQUIRED } from '../../isv.constants';

@Component({
  selector: 'app-registry-form',
  templateUrl: './registry-form.component.html',
  styleUrl: './registry-form.component.scss'
})
export class RegistryFormComponent implements OnInit, OnDestroy {

  @Input() panelTitle: string;
  @Input() registryForm: FormGroup;
  @Input() residenceForm: FormGroup;
  @Input() addressForm: FormGroup;
  @Input() identificationDocumentForm: FormGroup;
  @Input() businessForm: FormGroup;
  @Input() consentForm: FormGroup;
  @Input() properVerificationForm: FormGroup;
  @Input() isEffectiveOwner: boolean = false;
  @Input() isLegalGuardian: boolean = false;
  @Input() isEsecutoreConto: boolean = false;
  @Input() isReadOnly: boolean = false;

  @Input() saeList: IFormSelectOption[];
  @Input() taeList: IFormSelectOption[];
  @Input() atecoList: IFormSelectOption[];
  @Input() activityTypes: IFormSelectOption[];
  @Input() countriesList: IFormSelectOption[];
  @Input() provincesList: IFormSelectOption[];
  @Input() placesList: IFormSelectOption[];
  @Input() documentTypeList: IFormSelectOption[];
  @Input() documentReleaseAuthorityList: IFormSelectOption[];
  @Input() holderBeneficiaryRelationshipList: any[] = null;
  @Input() insuredBeneficiaryRelationshipList: IFormSelectOption[] = null;
  @Input() beneficiaryDeathTipologies: IFormSelectOption[] = null;
  @Input() beneficiaryLifeTipologies: IFormSelectOption[] = null;
  @Input() agreementPurposeList: IFormSelectOption[] = null;
  @Input() beneficiariesClassification: IFormSelectOption[] = null;
  @Input() businessTypes: IFormSelectOption[] = null;
  @Input() customerBehaviourList: IFormSelectOption[] = null;
  @Input() documentationList: IFormSelectOption[] = null;
  @Input() relazioneContraenteEsecutore: IFormSelectOption[] = null;
  @Input() tipoDelega: IFormSelectOption[] = null;
  @Input() fundsOriginList: IFormSelectOption[] = null;
  @Input() incomeLevelsList: IFormSelectOption[] = null;
  @Input() executionModeList: IFormSelectOption[] = null;
  @Input() operationExecutionModeList: IFormSelectOption[] = null;
  @Input() presenceInExternalList: IFormSelectOption[] = null;
  @Input() professionsList: IFormSelectOption[] = null;
  @Input() incomeBracketsList: IFormSelectOption[] = null;
  @Input() chargeTypes: IFormSelectOption[] = null;

  @Input() buttonsList: IModalButton[];

  @Output() actionRequested: EventEmitter<any> = new EventEmitter<any>();

  registryFormKeys: IFormKey[];
  residenceFormKeys: IFormKey[] = isv_contractor_residence_form_keys;
  addressFormKeys: IFormKey[] = isv_contractor_address_form_keys;
  identificationDocumentFormKeys: IFormKey[] = document_change_form_keys;
  businessFormKeys: IFormKey[];
  constentsFormKeys: IFormKey[];
  properVerificationFormKeys: IFormKey[];

  destroy$ = new Subject<void>();

  constructor(
    private readonly _spinnerService: SpinnerService,
    private readonly _policyService: PolicyService,
    private readonly _alertService: AlertService
  ) { }

  ngOnInit(): void {
    this._spinnerService.show();
    this.manageForms();
    this.manageFormKeys();
    this.watchFormsChanges();
    this._spinnerService.hide();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  manageForms(): void {
    if (this.registryForm) {
      const provinceValue = this.registryForm?.get('provinciaNascita')?.value;
      if (provinceValue) {
        const provinceCode = this.provincesList.find(province => province.value?.toLowerCase() === provinceValue?.toLowerCase());
        if (provinceCode) {
          this.registryForm?.get('provinciaNascita').setValue(provinceCode.key);
        }
      }
    }
    if (this.residenceForm) {
      const provinceValue = this.residenceForm?.get('provincia')?.value;
      if (provinceValue) {
        const provinceCode = this.provincesList.find(province => province.value?.toLowerCase() === provinceValue?.toLowerCase());
        if (provinceCode) {
          this.residenceForm?.get('provincia').setValue(provinceCode.key);
        }
      }
    }
    if (this.addressForm) {
      const provinceValue = this.addressForm?.get('provincia')?.value;
      if (provinceValue) {
        const provinceCode = this.provincesList.find(province => province.value?.toLowerCase() === provinceValue?.toLowerCase());
        if (provinceCode) {
          this.addressForm?.get('provincia').setValue(provinceCode.key);
        }
      }
    }
    if (this.businessForm) {
      const saeDesc: string = this.businessForm?.get('codiceSae')?.value?.toString();
      if (saeDesc) {
        const saeCode: string = this.saeList?.find(saeObj => saeObj.value.toLowerCase() === saeDesc?.toLowerCase())?.key || null;
        if (saeCode) {
          this.businessForm?.get('codiceSae').setValue(saeCode);
        }
      }
      if (this.isLegalSubject) {
        this.businessForm?.get('codiceAteco')?.enable();
        const atecoDesc: string = this.businessForm?.get('codiceAteco')?.value?.toString();
        if (atecoDesc) {
          const atecoCode: string = this.atecoList?.find(atecoObj => atecoObj.value.toLowerCase() === atecoDesc?.toLowerCase())?.key || null;
          if (atecoCode) {
            this.businessForm?.get('codiceAteco').setValue(atecoCode);
          }
        }
      } else {
        this.businessForm?.get('codiceAteco')?.clearValidators();
        this.businessForm?.get('codiceAteco')?.setValue(null);
        this.businessForm?.get('codiceAteco')?.disable();
      }
      
      this.businessForm?.get('activityType')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((selectedValue) => {
        if (selectedValue && this.activityTypes) {
            if (!TYPES_WITH_TAE_REQUIRED.includes(selectedValue)) {
                this.businessForm?.get('codiceTae')?.setValidators([Validators.required]);
                this.businessForm?.get('codiceTae')?.enable();
            } else {
                this.businessForm?.get('codiceTae')?.clearValidators();
                this.businessForm?.get('codiceTae')?.setValue(null); 
                this.businessForm?.get('codiceTae')?.disable();
            }
        }
      });
    }
    if (this.isReadOnly) {
      this.registryForm?.disable();
      this.residenceForm?.disable();
      this.addressForm?.disable();
      this.identificationDocumentForm?.disable();
      this.businessForm?.disable();
      this.consentForm?.disable();
      this.properVerificationForm?.disable();
    }
  }
  
  manageFormKeys(): void {
    // Registry form
    if (this.isEffectiveOwner) {
      this.registryFormKeys = effective_owner_registry_form_keys;
    } else if (this.isEsecutoreConto) {
      this.registryFormKeys = isv_legal_guardian_registry_form_keys;
    } else {
      if (this.isContractorChange) {
        this.registryFormKeys = this.isLegalSubject
        ? isv_new_contractor_PG_registry_form_keys
        : isv_new_contractor_registry_form_keys;
      } else {
        this.registryFormKeys = this.isLegalSubject
          ? isv_PG_contractor_registry_form_keys
          : isv_contractor_registry_form_keys;
      }
    }
    this.setOptions(this.registryFormKeys, 'cittadinanza', this.countriesList);
    this.setOptions(this.registryFormKeys, 'secondaCittadinanza', this.countriesList);
    this.setOptions(this.registryFormKeys, 'presenzaListaEsterne', this.presenceInExternalList);
    this.setOptions(this.registryFormKeys, 'svolgimentoRappContinuativo', this.executionModeList);
    this.setOptions(this.registryFormKeys, 'svolgimentoOperazione', this.operationExecutionModeList);
    this.setOptions(this.registryFormKeys, 'comportamentoCliente', this.customerBehaviourList);
    this.setOptions(this.registryFormKeys, 'statoNascita', this.countriesList);
    this.setOptions(this.registryFormKeys, 'isPEP', this.presenceInExternalList);
    this.setOptions(this.registryFormKeys, 'relazioneContraenteEsecutore', this.relazioneContraenteEsecutore)
    this.setOptions(this.registryFormKeys, 'tipoDelega', this.tipoDelega);
    this.setOptions(this.registryFormKeys, 'codiceSae', this.saeList);
    this.setOptions(this.registryFormKeys, 'codiceAteco', this.atecoList);

    const filterString: string = this.isLegalSubject ? 'Giuridica' : 'Fisica';
    const filteredHolderRelationship = this.holderBeneficiaryRelationshipList?.filter(option => option.additionalData?.beneficiaryType === filterString);
    this.setOptions(this.registryFormKeys, 'holderRelationship', filteredHolderRelationship);
    if (this.registryForm?.get('statoNascita')?.value === 'IT') {
      this.setOptions(this.registryFormKeys, 'provinciaNascita', this.provincesList);
      this.retrievePlaces(this.registryForm?.get('provinciaNascita')?.value, this.registryFormKeys, 'comuneNascita');
    }
    // Residence form
    this.setOptions(this.residenceFormKeys, 'stato', this.countriesList);
    if (this.residenceForm?.get('stato')?.value === 'IT') {
      this.setOptions(this.residenceFormKeys, 'provincia', this.provincesList);
      this.retrievePlaces(this.residenceForm?.get('provincia')?.value, this.residenceFormKeys, 'localita');
    }
    // Address form
    this.setOptions(this.addressFormKeys, 'stato', this.countriesList);
    if (this.addressForm?.get('stato')?.value === 'IT') {
      this.setOptions(this.addressFormKeys, 'provincia', this.provincesList);
      this.retrievePlaces(this.addressForm?.get('provincia')?.value, this.addressFormKeys, 'localita');
    }
    // Identification document form
    this.setOptions(this.identificationDocumentFormKeys, 'tipoDocumento', this.documentTypeList);
    this.setOptions(this.identificationDocumentFormKeys, 'enteRilascio', this.documentReleaseAuthorityList);

    // Business form
    if (this.isContractorChange) {
      this.businessFormKeys = this.registryForm?.get('partitaIva')?.value 
      ? isv_PG_new_contractor_business_form_keys 
      : isv_new_contractor_business_form_keys;
    } else {
      this.businessFormKeys = this.registryForm?.get('partitaIva')?.value
      ? isv_PG_contractor_business_form_keys
      : isv_contractor_business_form_keys;
    }
    if (this.saeList && this.atecoList) {

        this.setOptions(this.businessFormKeys, 'codiceSae', this.saeList);
        this.setOptions(this.businessFormKeys, 'codiceTae', this.taeList);
        this.setOptions(this.businessFormKeys, 'codiceAteco', this.atecoList);
        this.setOptions(this.businessFormKeys, 'activityType', this.activityTypes);
        this.setOptions(this.businessFormKeys, 'fasciaReddito', this.incomeLevelsList);
    }
    if (this.isContractorChange) {
      // Proper verification form
      this.properVerificationFormKeys = this.isEffectiveOwner || this.isLegalGuardian
        ? proper_verification_tutor_form_keys
        : proper_verification_form_keys;
      this.setOptions(this.properVerificationFormKeys, 'profession', this.professionsList);
      this.setOptions(this.properVerificationFormKeys, 'fasciaReddito', this.incomeLevelsList);
      this.chargeTypes?.forEach((chargeType) => {
        if (!this.properVerificationFormKeys.some(key => key.controlName === chargeType.key)) {        
            const key: IFormKey = {
              id: chargeType.key + '-field',
              label: chargeType.value,
              controlName: chargeType.key,
              css: 'col-6',
              type: 'CHECKBOX'
            };
            this.properVerificationFormKeys?.push(key)
        }
      })
      
      // Consents form
      this.constentsFormKeys = this.isLegalSubject
        ? isv_contractor_PG_consents_form_keys
        : isv_contractor_consents_form_keys;
    }
    this.setOptions(this.businessFormKeys, 'classeBeneficiariSuCuiSiRivolgonoLeAttivita', this.beneficiariesClassification);
    this.setOptions(this.businessFormKeys, 'tipoSocietaImpresa', this.businessTypes);
  }
   
  setOptions(keys: IFormKey[], controlName: string, options: IFormSelectOption[]) {
    if (!keys) {
      return;
    }
    const key = keys.find(k => k.controlName === controlName);
    if (key) {
      key.options = options;
    }
  }

  watchFormsChanges(): void {
    if (!this.addressForm) {
      return;
    }
    this.addressForm?.get('sameAsResidence')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedValue) => {
        if (selectedValue) {
          this.addressForm.patchValue(this.residenceForm.getRawValue());
        }
        this.toggleAddressFormControls(!selectedValue);
      });
    this.registryForm?.get('statoNascita')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedValue) => {
        if (selectedValue === "IT") {
          this.registryForm?.get('provinciaNascita').enable();
          this.setOptions(this.registryFormKeys, 'provinciaNascita', this.provincesList);
        } else {
          this.setOptions(this.registryFormKeys, 'provinciaNascita', []);
          this.registryForm?.get('provinciaNascita').setValue(null);
          this.registryForm?.get('provinciaNascita').disable();
        }
      });
    this.registryForm?.get('provinciaNascita')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedValue) => {
        this.retrievePlaces(selectedValue, this.registryFormKeys, 'comuneNascita');
      });
  }

  retrievePlaces(provinceCode: string, formKeysObj: IFormKey[], controlName: string): void {
    this._spinnerService.show();
    this._policyService.retrieveCities(provinceCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: { listaComuni: ICityObject[] }) => {
          if (response) {
            const placesList = response.listaComuni?.map((cityObj: ICityObject) => ({
              key: cityObj.cityName,
              value: cityObj.cityName
            }));
            this.setOptions(formKeysObj, controlName, placesList);
          }
          this._spinnerService.hide();
        },
        error: error => {
          LoggerService.logError(this.constructor.name, 'retirevePlaces', error);
          this._alertService.openErrorMessage('Errore nel recupero della lista delle città', 'retirevePlaces', error);
          this._spinnerService.hide();
        }
      })
  }
  
  toggleAddressFormControls(enable: boolean): void {
    this.addressFormKeys.forEach((key) => {
      if (key.controlName && key.controlName !== 'sameAsResidence') {
        const control = this.addressForm.get(key.controlName);
        enable ? control.enable() : control.disable();
      }
    });
  }

  onActionRequested(button: IModalButton): void {
    if (button?.output === EModalOutput.RESET) {
      this.registryForm?.reset();
      this.residenceForm?.reset();
      this.addressForm?.reset();
      this.identificationDocumentForm?.reset();
    } else if (button?.output === EModalOutput.REMOVE) {
      this.actionRequested.emit(EModalOutput.REMOVE);
    }
  }

  get showForms(): boolean {
    return true;
  }

  get isLegalSubject(): boolean {
    return !!this.registryForm?.get('partitaIva');
  }

  get operationId(): string {
    return this._policyService?.operationId;
  }
  
  get isContractorChange(): boolean {
    return this.operationId === '_VCONT';
  }

}
